import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import type { Profile } from "../types";
import { StockImageSuggestions } from "./StockImageSuggestions";
import { FieldSyncBadge } from "./FieldSyncBadge";
import { Aside, AsideCard, Badge as Selo, Botao, BotaoPequeno, Coluna, EditorGrid, Hint, Lab } from "./EditorChrome";
import {
  mergeGabaritoComPublicacao,
  readGabaritoState,
  writeGabaritoState,
} from "./gabarito-utils";
import { profilesTrackingService } from "../../../services";
import {
  seoProfileApi,
  type GoogleConnection,
  type SeoCounts,
  type SeoJob,
  type SeoResearch,
  type SeoResearchItem,
} from "../../../services/seo";
import { jobBloqueiaEsteModo } from "./seo-publicacao";
import {
  BOTOES_DO_PRODUTO,
  linkDoWhatsApp,
  linkInvalido,
  normalizarLink,
} from "./seo-produto-link";
import { avisoDaFoto, prepararFotoParaGoogle } from "./foto-para-google";

type SeoTabProps = {
  profile: Profile;
};

type EditableItem = SeoResearchItem & {
  id: string;
  selected: boolean;
  confirmed: boolean;
  /** Botão do produto no Google e o link dele (site, página ou WhatsApp). */
  botao?: string;
  link?: string;
  image?: {
    name: string;
    contentType: string;
    dataUrl: string;
  };
};

type Mode = "services" | "products" | "service_descriptions";

/* Prancheta PerfilSeo: coluna 8/4, KPIs em cartão dividido, itens em tabela, aside com passos. */

const Titulo = styled.h3`
  margin: 0;
  font-family: var(--font-sans);
  font-size: 22px;
  line-height: 28px;
  font-weight: 500;
  color: #1f1f1f;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Kpis = styled.div`
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  overflow: hidden;
  .kpi {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 14px 18px;
    border-right: 1px solid #eef1f4;
    min-width: 0;
  }
  .kpi:last-child {
    border-right: 0;
  }
  .kpi b {
    font-family: var(--font-sans);
    font-size: 24px;
    font-weight: 500;
    letter-spacing: -0.5px;
  }
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    .kpi {
      border-bottom: 1px solid #eef1f4;
    }
  }
`;

const Cartao = styled.section`
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 16px;
  overflow: hidden;
`;

const CabecalhoDoCartao = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--line);
  background: #f8fafc;
  flex-wrap: wrap;
  .titulo {
    font-family: var(--font-sans);
    font-size: 14px;
    font-weight: 600;
    color: #1f1f1f;
  }
`;

const CorpoDoCartao = styled.div`
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CampoSelect = styled.label`
  display: inline-flex;
  flex-direction: column;
  gap: 6px;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.3px;
  color: #3c4043;
  text-transform: uppercase;
  select {
    height: 40px;
    padding: 0 36px 0 12px;
    border: 1px solid #dadce0;
    border-radius: 10px;
    font-size: 14px;
    font-family: inherit;
    text-transform: none;
    letter-spacing: 0;
    color: #1f1f1f;
    background: #fff url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='%235F6368'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E") no-repeat right 10px center;
    appearance: none;
  }
`;

const Diagnostico = styled.div`
  padding: 14px 16px;
  border-radius: 12px;
  background: #eaf1fb;
  font-size: 14px;
  line-height: 21px;
  color: #1f1f1f;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const GabaritoChip = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? "#EAF1FB" : "#E2E6EA")};
  background: ${(p) => (p.$active ? "#EAF1FB" : "#fff")};
  color: ${(p) => (p.$active ? "#0B4FA8" : "#2B2D31")};
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  .material-icons {
    font-size: 14px;
  }
`;

const Linha = styled.article<{ $blocked?: boolean }>`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr);
  gap: 14px;
  padding: 14px 18px;
  border-bottom: 1px solid #eef1f4;
  background: ${(p) => (p.$blocked ? "#FFFBEB" : "#fff")};
  &:last-child {
    border-bottom: 0;
  }
  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: #1264c7;
    margin-top: 12px;
  }
`;

const Campos = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
  gap: 12px;
  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.3px;
  color: #3c4043;
  text-transform: uppercase;
  min-width: 0;
`;

const Input = styled.input`
  height: 44px;
  padding: 0 14px;
  border: 1px solid #e2e6ea;
  border-radius: 12px;
  font-size: 14px;
  font-family: inherit;
  color: #1f1f1f;
  text-transform: none;
  letter-spacing: 0;
  &:focus {
    outline: none;
    border-color: #1264c7;
    box-shadow: 0 0 0 2px rgba(18, 100, 199, 0.25);
  }
  &[aria-invalid="true"] {
    border-color: #d93025;
  }
`;

const Textarea = styled.textarea`
  min-height: 72px;
  padding: 10px 14px;
  border: 1px solid #e2e6ea;
  border-radius: 12px;
  font-size: 14px;
  line-height: 20px;
  font-family: inherit;
  color: #1f1f1f;
  text-transform: none;
  letter-spacing: 0;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: #1264c7;
    box-shadow: 0 0 0 2px rgba(18, 100, 199, 0.25);
  }
`;

const Select = styled.select`
  height: 44px;
  padding: 0 36px 0 12px;
  border: 1px solid #e2e6ea;
  border-radius: 12px;
  font-size: 14px;
  font-family: inherit;
  text-transform: none;
  letter-spacing: 0;
  color: #1f1f1f;
  background: #fff url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='%235F6368'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E") no-repeat right 10px center;
  appearance: none;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-bottom: 10px;
`;

const ConfirmLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #fff7e0;
  border: 1px solid #fde293;
  font-size: 13px;
  line-height: 19px;
  color: #5f4200;
  input {
    margin-top: 2px;
    accent-color: #1264c7;
  }
`;

const ProductPhoto = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  flex-wrap: wrap;
  img {
    width: 72px;
    height: 56px;
    border-radius: 8px;
    object-fit: cover;
    background: #eaf1fb;
  }
`;

const PhotoPicker = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px dashed #c2d7f5;
  color: #1264c7;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  input {
    display: none;
  }
  .material-icons {
    font-size: 18px;
  }
`;

const PhotoMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  strong {
    font-size: 13px;
    font-weight: 500;
  }
  span {
    font-size: 12px;
    color: #5f6368;
  }
`;

const LinkRow = styled.div`
  display: grid;
  grid-template-columns: 180px minmax(0, 1fr);
  gap: 12px;
  margin-top: 12px;
  align-items: start;
  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const LinkShortcuts = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 6px;
  flex-wrap: wrap;
`;

const ShortcutButton = styled.button`
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid #e2e6ea;
  background: #fff;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  color: #0b4fa8;
  cursor: pointer;
  &:hover {
    background: #f8fafd;
  }
`;

const LinkError = styled.small`
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #b3261e;
`;

const Notice = styled.div<{ $tone?: "warning" | "error" | "success" }>`
  display: flex;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 19px;
  background: ${(p) => (p.$tone === "error" ? "#FCE8E6" : p.$tone === "success" ? "#EAF7EE" : "#FFF7E0")};
  border: 1px solid ${(p) => (p.$tone === "error" ? "#F6AEA9" : p.$tone === "success" ? "#A8DAB5" : "#FDE293")};
  color: ${(p) => (p.$tone === "error" ? "#B3261E" : p.$tone === "success" ? "#137333" : "#5F4200")};
  .material-icons {
    font-size: 18px;
    flex-shrink: 0;
  }
`;

const PublishBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 18px;
  border-top: 1px solid var(--line);
  background: #f8fafc;
  flex-wrap: wrap;
`;

const ToggleLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex: 1 1 320px;
  font-size: 13px;
  font-weight: 500;
  color: #1f1f1f;
  input {
    margin-top: 3px;
    accent-color: #1264c7;
  }
  span {
    display: block;
    font-size: 12px;
    font-weight: 400;
    color: #5f6368;
  }
`;

const ProgressTrack = styled.div`
  height: 8px;
  border-radius: 999px;
  background: #eef1f4;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $value: number }>`
  width: ${(p) => p.$value}%;
  height: 100%;
  background: #1e8e3e;
  transition: width 0.3s;
`;

const Log = styled.pre`
  margin: 0;
  max-height: 160px;
  overflow: auto;
  padding: 10px 12px;
  border-radius: 10px;
  background: #1f1f1f;
  color: #e8eaed;
  font-size: 12px;
  line-height: 18px;
  white-space: pre-wrap;
`;

const Empty = styled.div`
  padding: 28px 18px;
  text-align: center;
  font-size: 14px;
  color: #5f6368;
`;

const Passo = styled.div`
  display: flex;
  gap: 10px;
  font-size: 13px;
  line-height: 18px;
  color: #2b2d31;
  b {
    width: 22px;
    height: 22px;
    border-radius: 999px;
    background: #eaf1fb;
    color: #0b4fa8;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    flex-shrink: 0;
  }
`;

// Produtos começam DESMARCADOS: o operador marca só o que vai publicar de
// verdade (Reinaldo, 14/09/2026). Marcados, com foto e confirmação pendentes,
// eles travavam o botão até o operador desmarcar um por um.
const buildEditableItems = (result: SeoResearch): EditableItem[] => {
  const researchItems: EditableItem[] = result.itens.map((item, index) => ({
    ...item,
    id: `${item.uso}-${index}-${item.nome}`,
    selected: item.uso !== "produto",
    confirmed: !item.confirmar,
  }));
  const knownProducts = new Set(
    researchItems
      .filter((item) => item.uso === "produto")
      .map((item) => item.nome.trim().toLocaleLowerCase("pt-BR")),
  );
  const productItems: EditableItem[] = result.produtos
    .filter(
      (product) =>
        !knownProducts.has(product.nome.trim().toLocaleLowerCase("pt-BR")),
    )
    .map((product, index) => ({
      keyword: product.cobre[0] || product.nome,
      nome: product.nome,
      descricao: product.descricao,
      uso: "produto",
      intencao: "alta",
      prioridade: 2,
      confirmar: true,
      motivoConfirmar:
        "Confirme que este produto é vendido pelo perfil e selecione uma foto real.",
      id: `produto-extra-${index}-${product.nome}`,
      selected: false,
      confirmed: false,
    }));
  return [...researchItems, ...productItems];
};

const NOME_DO_MODO: Record<Mode, string> = {
  services: "serviços",
  products: "produtos",
  service_descriptions: "descrições de serviços",
};

const TITULO_DO_MODO: Record<Mode, string> = {
  services: "Serviços",
  products: "Produtos",
  service_descriptions: "Descrições de serviços",
};

const errorMessage = (error: unknown) => {
  const data = (
    error as {
      response?: { data?: { message?: string; userMessage?: string } };
    }
  )?.response?.data;
  return data?.userMessage || data?.message || (error instanceof Error ? error.message : "Erro inesperado.");
};

export const SeoTab = ({ profile }: SeoTabProps) => {
  const [research, setResearch] = useState<SeoResearch | null>(null);
  const [items, setItems] = useState<EditableItem[]>([]);
  const [counts, setCounts] = useState<SeoCounts>({
    services: 0,
    products: 0,
    today: 0,
    dailyCap: 30,
  });
  const [mode, setMode] = useState<Mode>("services");
  const [dryRun, setDryRun] = useState(true);
  // Um job por tipo: enviar os produtos não pode apagar o acompanhamento dos
  // serviços, que seguem na fila da extensão.
  const [jobs, setJobs] = useState<Partial<Record<Mode, SeoJob>>>({});
  const [loadingResearch, setLoadingResearch] = useState(false);
  const [loadingPublish, setLoadingPublish] = useState(false);
  // ID do operador — precisa ser IGUAL ao "Seu ID de operador" da extensão do
  // Chrome. É por ele que o radar entrega o job pro navegador certo. Persistido.
  const [operatorId, setOperatorId] = useState(() =>
    typeof localStorage !== "undefined"
      ? localStorage.getItem("gr_operator_id") || ""
      : "",
  );
  const [feedback, setFeedback] = useState<{
    tone: "warning" | "error" | "success";
    message: string;
  } | null>(null);
  const [gabaritoKeywords, setGabaritoKeywords] = useState<string[]>([]);
  const [selectedGabarito, setSelectedGabarito] = useState<string[]>([]);
  const [googleConn, setGoogleConn] = useState<GoogleConnection | null>(null);

  useEffect(() => {
    let active = true;
    seoProfileApi
      .getCounts(profile.id)
      .then((value) => active && setCounts(value))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [profile.id]);

  useEffect(() => {
    let active = true;
    seoProfileApi
      .getGabaritoKeywords(profile.id)
      .then((value) => active && setGabaritoKeywords(value))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [profile.id]);

  useEffect(() => {
    let active = true;
    seoProfileApi
      .getGoogleConnection(profile.id)
      .then((value) => active && setGoogleConn(value))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [profile.id]);

  useEffect(() => {
    let active = true;
    // Outro perfil: nada do anterior fica na tela (itens, fotos, links, jobs).
    setResearch(null);
    setItems([]);
    setJobs({});
    setFeedback(null);
    setLoadingPublish(false);
    seoProfileApi
      .getLatestResearch(profile.id)
      .then((result) => {
        if (!active || !result || !result.itens) return;
        setResearch(result);
        setItems(buildEditableItems(result));
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [profile.id]);

  // "modo:jobId" de cada job ainda na fila; muda quando um termina e o
  // acompanhamento dele para sozinho.
  const jobsEmAndamento = (Object.values(jobs) as SeoJob[])
    .filter((item) => ["QUEUED", "CLAIMED", "RUNNING"].includes(item.status))
    .map((item) => `${item.mode}:${item.jobId}`)
    .join(",");

  useEffect(() => {
    if (!jobsEmAndamento) return;
    const acompanhados = jobsEmAndamento.split(",").map((par) => {
      const [modo, jobId] = par.split(":");
      return { modo: modo as Mode, jobId };
    });
    const interval = window.setInterval(async () => {
      for (const { modo, jobId } of acompanhados) {
        try {
          const next = await seoProfileApi.getStatus(jobId);
          setJobs((atual) => ({ ...atual, [modo]: next }));
          if (["COMPLETED", "FAILED"].includes(next.status)) {
            setFeedback({
              tone: next.status === "COMPLETED" ? "success" : "error",
              message:
                next.status === "COMPLETED"
                  ? next.dryRun
                    ? `Simulação de ${NOME_DO_MODO[modo]} concluída. Revise o log antes de publicar.`
                    : `Atualização de ${NOME_DO_MODO[modo]} concluída.`
                  : next.error || "O job foi interrompido.",
            });
            const nextCounts = await seoProfileApi.getCounts(profile.id);
            setCounts(nextCounts);
          }
        } catch (error) {
          const message = errorMessage(error);
          setJobs((atual) => {
            const falhou = atual[modo];
            return falhou
              ? { ...atual, [modo]: { ...falhou, status: "FAILED", error: message } }
              : atual;
          });
          setFeedback({ tone: "error", message });
        }
      }
    }, 1500);
    return () => window.clearInterval(interval);
  }, [jobsEmAndamento, profile.id]);

  const visibleItems = useMemo(
    () =>
      items.filter((item) =>
        mode === "products" ? item.uso === "produto" : item.uso === "servico",
      ),
    [items, mode],
  );

  const selectedItems = visibleItems.filter((item) => item.selected);
  const hasBlocked = selectedItems.some(
    (item) => item.confirmar && !item.confirmed,
  );
  const hasProductWithoutPhoto =
    mode === "products" && selectedItems.some((item) => !item.image);
  const hasInvalidLink =
    mode === "products" && selectedItems.some((item) => linkInvalido(item.link));
  // Só o job DESTA aba trava o botão: publicar serviços deixava produtos
  // travado até a extensão puxar a fila, e o operador tinha que refazer a
  // seleção inteira para conseguir publicar os produtos.
  const jobRunning = jobBloqueiaEsteModo(jobs[mode], mode);
  const itemsReady =
    selectedItems.length > 0 &&
    !hasBlocked &&
    !hasProductWithoutPhoto &&
    !hasInvalidLink &&
    !loadingPublish &&
    !jobRunning;
  // A publicação é feita pela EXTENSÃO do Chrome no navegador do operador que
  // gerencia o perfil (não pelo worker) — então não depende mais de worker online.
  const canPublish = itemsReady;

  const runResearch = async () => {
    setLoadingResearch(true);
    setFeedback(null);
    try {
      const result = await seoProfileApi.research(profile.id);
      setResearch(result);
      const built = buildEditableItems(result);
      setItems(built);
      setFeedback({
        tone: "success",
        message: `${built.length} oportunidades encontradas. Revise nomes, descrições e confirmações.`,
      });
    } catch (error) {
      setFeedback({ tone: "error", message: errorMessage(error) });
    } finally {
      setLoadingResearch(false);
    }
  };

  const updateItem = (id: string, patch: Partial<EditableItem>) => {
    setItems((current) =>
      current.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
  };

  const setProductPhoto = (id: string, file?: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setFeedback({
        tone: "error",
        message: "Selecione um arquivo de imagem válido.",
      });
      return;
    }
    // O Google não aceita WebP nem foto pequena: converte/amplia antes de guardar.
    prepararFotoParaGoogle(file)
      .then((foto) => {
        updateItem(id, {
          image: {
            name: foto.nome,
            contentType: foto.contentType,
            dataUrl: foto.dataUrl,
          },
        });
        const aviso = avisoDaFoto(foto);
        if (aviso) setFeedback({ tone: "success", message: aviso });
      })
      .catch((erro: unknown) => {
        setFeedback({
          tone: "error",
          message: erro instanceof Error ? erro.message : "Não foi possível ler a imagem.",
        });
      });
  };

  const addManualProduct = () => {
    const timestamp = Date.now();
    setItems((current) => [
      ...current,
      {
        keyword: "produto solar",
        nome: "Novo produto",
        descricao: "",
        uso: "produto",
        intencao: "alta",
        prioridade: 1,
        confirmar: true,
        motivoConfirmar:
          "Confirme que este produto é vendido pelo perfil e selecione uma foto real.",
        id: `produto-manual-${timestamp}`,
        selected: true,
        confirmed: false,
      },
    ]);
  };

  const toggleGabarito = (keyword: string) => {
    setSelectedGabarito((current) =>
      current.includes(keyword)
        ? current.filter((value) => value !== keyword)
        : [...current, keyword],
    );
  };

  const applyGabaritoToPlan = () => {
    if (!selectedGabarito.length) return;
    const uso: SeoResearchItem["uso"] =
      mode === "products" ? "produto" : "servico";
    const existing = new Set(
      items.map((item) => item.nome.trim().toLocaleLowerCase("pt-BR")),
    );
    const timestamp = Date.now();
    const isProduct = uso === "produto";
    const additions: EditableItem[] = selectedGabarito
      .filter(
        (keyword) =>
          !existing.has(keyword.trim().toLocaleLowerCase("pt-BR")),
      )
      .map((keyword, index) => ({
        keyword,
        nome: keyword,
        descricao: "",
        uso,
        intencao: "alta",
        prioridade: 1,
        confirmar: isProduct,
        motivoConfirmar: isProduct
          ? "Confirme que este produto é vendido pelo perfil e selecione uma foto real."
          : "",
        id: `gabarito-${timestamp}-${index}-${keyword}`,
        selected: true,
        confirmed: !isProduct,
      }));
    if (!additions.length) {
      setFeedback({
        tone: "warning",
        message: "As palavras selecionadas já estão no plano.",
      });
      return;
    }
    setItems((current) => [...current, ...additions]);
    setSelectedGabarito([]);
    setFeedback({
      tone: "success",
      message: `${additions.length} palavra(s) do Gabarito adicionada(s) ao plano.`,
    });
  };

  const publish = async () => {
    if (!canPublish) return;
    // O ID do operador é obrigatório também no Simular: o radar entrega o job à
    // extensão pelo operatorId. Sem ele, a extensão do operador não recebe nada.
    if (!operatorId.trim()) {
      setFeedback({
        tone: "error",
        message:
          "Informe o ID do operador (o mesmo configurado na extensão) antes de continuar.",
      });
      return;
    }
    setLoadingPublish(true);
    setFeedback(null);
    try {
      const nextJob = await seoProfileApi.publish({
        profileId: profile.id,
        mode,
        dryRun,
        operatorId: operatorId.trim(),
        items: selectedItems.map((item) => {
          const buttonUrl =
            mode === "products"
              ? (normalizarLink(item.link) ?? undefined)
              : undefined;
          return {
            name: item.nome,
            description: item.descricao,
            requiresConfirmation: item.confirmar,
            confirmed: item.confirmed,
            imageName: item.image?.name,
            imageContentType: item.image?.contentType,
            imageDataUrl: item.image?.dataUrl,
            buttonType: buttonUrl ? item.botao || "SAIBA_MAIS" : undefined,
            buttonUrl,
          };
        }),
      });
      setJobs((atual) => ({ ...atual, [nextJob.mode || mode]: nextJob }));
      // O envio terminou: o job segue na fila da extensão, mas a tela fica
      // livre para o outro tipo. Antes o botão só voltava quando a extensão
      // concluía — o operador salvava e reabria o perfil para mandar produtos.
      setLoadingPublish(false);
      if (!dryRun) {
        try {
          const publicadas = selectedItems
            .map((item) => item.nome.trim())
            .filter(Boolean);
          if (publicadas.length) {
            const remote = await profilesTrackingService
              .getGabarito(profile.id)
              .catch(() => null);
            const atualizado = mergeGabaritoComPublicacao(
              remote,
              readGabaritoState(profile.id),
              publicadas,
            );
            writeGabaritoState(profile.id, atualizado);
            await profilesTrackingService.saveGabaritoText(profile.id, {
              postKeywords: atualizado.postKeywords,
              commentKeywords: atualizado.commentKeywords,
              postNotes: atualizado.postNotes,
              commentNotes: atualizado.commentNotes,
            });
            const refreshed = await seoProfileApi
              .getGabaritoKeywords(profile.id)
              .catch(() => null);
            if (refreshed) setGabaritoKeywords(refreshed);
          }
        } catch {
          setFeedback({
            tone: "warning",
            message:
              "Publicação enviada, mas não consegui gravar as palavras no Gabarito — confira a aba Gabarito.",
          });
        }
      }
    } catch (error) {
      setLoadingPublish(false);
      setFeedback({ tone: "error", message: errorMessage(error) });
    }
  };

  const revisar = selectedItems.filter((item) => item.confirmar && !item.confirmed).length;
  const semFoto = selectedItems.filter((item) => item.uso === "produto" && !item.image).length;
  const servicosNoPlano = items.filter((item) => item.uso !== "produto" && item.selected).length;
  const produtosNoPlano = items.filter((item) => item.uso === "produto" && item.selected).length;
  const tituloDoCartao =
    mode === "products" ? "Produtos a preparar" : mode === "service_descriptions" ? "Descrições a atualizar" : "Serviços a cadastrar";
  const apoioDoCartao =
    mode === "products"
      ? "Cada produto precisa de foto e link válido (site, página ou WhatsApp) para ser publicado."
      : mode === "service_descriptions"
        ? "Descrições curtas para serviços que já estão na ficha."
        : "Nome, categoria e descrição curta. Duplicidades com a ficha são removidas automaticamente.";
  const pendencias = [
    ...selectedItems.filter((item) => item.confirmar && !item.confirmed).map((item) => ({ tom: "aviso" as const, texto: `"${item.nome}" precisa de confirmação: ${item.motivoConfirmar || "confirme que o perfil oferece este item."}` })),
    ...selectedItems.filter((item) => item.uso === "produto" && !item.image).map((item) => ({ tom: "erro" as const, texto: `"${item.nome}" está sem foto.` })),
    ...selectedItems.filter((item) => item.uso === "produto" && linkInvalido(item.link)).map((item) => ({ tom: "erro" as const, texto: `"${item.nome}" está com link inválido.` })),
  ];
  const primeiroServico = items.find((item) => item.uso !== "produto" && item.selected);
  const primeiroProduto = items.find((item) => item.uso === "produto" && item.selected);

  return (
    <EditorGrid style={{ gridTemplateColumns: "minmax(0, 8fr) minmax(0, 4fr)" }}>
      <Coluna style={{ gap: 20 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Titulo>
              Plano de atualização · serviços e produtos
              <FieldSyncBadge kind="backend" />
            </Titulo>
            <Hint>O que será cadastrado na ficha do Google pela extensão do Chrome, dentro do limite diário. Itens vêm do Gabarito e das sugestões da IA; você revisa antes.</Hint>
          </div>
          <Botao aria-label="Pesquisar oportunidades com IA" type="button" $variante="filled" onClick={runResearch} disabled={loadingResearch}>
            <span className="material-icons">auto_awesome</span>
            {loadingResearch ? "Pesquisando..." : "Pesquisar oportunidades com IA"}
          </Botao>
        </div>

        <Kpis>
          <div className="kpi">
            <Hint>Serviços no plano</Hint>
            <b>{servicosNoPlano}</b>
            <Hint>{`${counts.services} já na ficha${revisar && mode !== "products" ? ` · ${revisar} revisar` : ""}`}</Hint>
          </div>
          <div className="kpi">
            <Hint>Produtos no plano</Hint>
            <b>{produtosNoPlano}</b>
            <Hint>{semFoto > 0 ? `${semFoto} sem foto` : `${counts.products} já na ficha`}</Hint>
          </div>
          <div className="kpi">
            <Hint>Publicados hoje</Hint>
            <b>
              {counts.today}
              <span style={{ fontSize: 16, color: "#5F6368" }}>{`/${counts.dailyCap}`}</span>
            </b>
            <Hint>limite diário por perfil</Hint>
          </div>
          <div className="kpi">
            <Hint>Publicação</Hint>
            <b style={{ fontSize: 16 }}>Extensão do Chrome</b>
            {googleConn ? (
              <Selo $tom={googleConn.connected ? "verde" : "amarelo"} style={{ alignSelf: "flex-start" }}>
                {googleConn.connected ? "Conta conectada" : googleConn.hasToken ? "Reconectar" : "Não conectada"}
              </Selo>
            ) : (
              <Hint>conta do perfil</Hint>
            )}
          </div>
        </Kpis>

        {googleConn && !googleConn.connected && (
          <Notice $tone="warning">
            <span className="material-icons">sync_problem</span>
            <span style={{ flex: 1 }}>{`A conta Google do perfil (${googleConn.email || "não vinculada"}) não está conectada. Reconecte antes de publicar.`}</span>
            <BotaoPequeno type="button" $variante="outlined" onClick={() => seoProfileApi.reconnectGoogle()}>
              <span className="material-icons">sync</span>
              Reconectar conta Google
            </BotaoPequeno>
          </Notice>
        )}

        {feedback && (
          <Notice $tone={feedback.tone}>
            <span className="material-icons">{feedback.tone === "error" ? "error" : feedback.tone === "success" ? "check_circle" : "info"}</span>
            {feedback.message}
          </Notice>
        )}

        {research ? (
          <>
            <Cartao>
              <CabecalhoDoCartao>
                <span className="titulo">Diagnóstico da pesquisa</span>
                <Selo $tom="cinza">
                  <span className="material-icons">insights</span>
                  IA · {research.itens.length} itens
                </Selo>
              </CabecalhoDoCartao>
              <CorpoDoCartao>
                <Diagnostico>{research.diagnostico}</Diagnostico>
                <ChipRow>
                  {research.grupos.flatMap((group) =>
                    group.keywords.slice(0, 8).map((keyword) => (
                      <Selo key={`${group.grupo}-${keyword}`} $tom="cinza">{keyword}</Selo>
                    )),
                  )}
                </ChipRow>
              </CorpoDoCartao>
            </Cartao>

            {gabaritoKeywords.length > 0 && (
              <Cartao>
                <CabecalhoDoCartao>
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <span className="titulo">Palavras do Gabarito <FieldSyncBadge kind="backend" /></span>
                    <Hint>{`Selecione palavras do Gabarito e aplique como ${mode === "products" ? "produtos" : "serviços"} no plano de atualização.`}</Hint>
                  </div>
                  <BotaoPequeno type="button" $variante="filled" onClick={applyGabaritoToPlan} disabled={selectedGabarito.length === 0}>
                    <span className="material-icons">playlist_add</span>
                    {`Aplicar ${selectedGabarito.length} no plano`}
                  </BotaoPequeno>
                </CabecalhoDoCartao>
                <CorpoDoCartao>
                  <ChipRow>
                    {gabaritoKeywords.map((keyword) => (
                      <GabaritoChip key={keyword} type="button" $active={selectedGabarito.includes(keyword)} aria-pressed={selectedGabarito.includes(keyword)} onClick={() => toggleGabarito(keyword)}>
                        {selectedGabarito.includes(keyword) && <span className="material-icons">check</span>}
                        {keyword}
                      </GabaritoChip>
                    ))}
                  </ChipRow>
                </CorpoDoCartao>
              </Cartao>
            )}

            <Cartao>
              <CabecalhoDoCartao>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                  <span className="titulo">{tituloDoCartao}</span>
                  <Hint>{apoioDoCartao}</Hint>
                </div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 8, flexWrap: "wrap" }}>
                  <CampoSelect>
                    Tipo de atualização
                    <select value={mode} onChange={(event) => setMode(event.target.value as Mode)}>
                      <option value="services">Cadastrar serviços</option>
                      <option value="service_descriptions">Atualizar descrições de serviços</option>
                      <option value="products">Preparar produtos</option>
                    </select>
                  </CampoSelect>
                  {mode === "products" && (
                    <BotaoPequeno aria-label="Adicionar produto" type="button" $variante="outlined" onClick={addManualProduct}>
                      <span className="material-icons">add</span>
                      Adicionar produto
                    </BotaoPequeno>
                  )}
                  <Selo style={{ alignSelf: "center" }}>{`${selectedItems.length} selecionado(s)`}</Selo>
                </div>
              </CabecalhoDoCartao>

              {visibleItems.length ? (
                <div>
                  {visibleItems.map((item) => {
                    const blocked = item.confirmar && !item.confirmed;
                    return (
                      <Linha key={item.id} $blocked={blocked}>
                        <input
                          aria-label={`Selecionar ${item.nome}`}
                          type="checkbox"
                          checked={item.selected}
                          onChange={(event) => updateItem(item.id, { selected: event.target.checked })}
                        />
                        <div style={{ minWidth: 0 }}>
                          <MetaRow>
                            <Selo $tom={item.prioridade <= 1 ? "verde" : item.prioridade === 2 ? "azul" : "cinza"}>{`Prioridade ${item.prioridade}`}</Selo>
                            <Selo $tom="cinza">{`${item.intencao} intenção`}</Selo>
                            <Selo>{item.keyword}</Selo>
                            {blocked ? (
                              <Selo $tom="amarelo"><span className="material-icons">help</span>Confirmar</Selo>
                            ) : item.uso === "produto" && !item.image ? (
                              <Selo $tom="amarelo"><span className="material-icons">image_not_supported</span>Sem foto</Selo>
                            ) : (
                              <Selo $tom="verde"><span className="material-icons">check_circle</span>Pronto</Selo>
                            )}
                          </MetaRow>
                          <Campos>
                            <Field>
                              Nome
                              <Input value={item.nome} maxLength={item.uso === "produto" ? 58 : 120} onChange={(event) => updateItem(item.id, { nome: event.target.value })} />
                            </Field>
                            <Field>
                              Descrição
                              <Textarea value={item.descricao} maxLength={item.uso === "produto" ? 1000 : 300} onChange={(event) => updateItem(item.id, { descricao: event.target.value })} />
                            </Field>
                          </Campos>
                          {item.uso === "produto" && (
                            <ProductPhoto>
                              {item.image && <img src={item.image.dataUrl} alt={`Prévia de ${item.nome}`} />}
                              <PhotoPicker>
                                <span className="material-icons">add_photo_alternate</span>
                                {item.image ? "Trocar foto" : "Adicionar foto"}
                                <input
                                  aria-label={`Foto do produto ${item.nome}`}
                                  type="file"
                                  accept="image/*"
                                  onChange={(event) => setProductPhoto(item.id, event.target.files?.[0])}
                                />
                              </PhotoPicker>
                              <PhotoMeta>
                                <strong>{item.image?.name || "Foto obrigatória"}</strong>
                                <span>Qualquer foto: o sistema converte para JPG quando o Google não aceita o formato. A foto será enviada junto do produto.</span>
                              </PhotoMeta>
                            </ProductPhoto>
                          )}
                          {item.uso === "produto" && (
                            <StockImageSuggestions keyword={item.nome} city={profile.city} locationHint={item.keyword} onPick={(image) => updateItem(item.id, { image })} />
                          )}
                          {item.uso === "produto" && (
                            <LinkRow>
                              <Field>
                                Botão
                                <Select aria-label={`Botão de ${item.nome}`} value={item.botao || "SAIBA_MAIS"} onChange={(event) => updateItem(item.id, { botao: event.target.value })}>
                                  {BOTOES_DO_PRODUTO.map((botao) => (
                                    <option key={botao.valor} value={botao.valor}>
                                      {botao.rotulo}
                                    </option>
                                  ))}
                                </Select>
                              </Field>
                              <div>
                                <Field>
                                  Link do botão (opcional)
                                  <Input
                                    aria-label={`Link do botão de ${item.nome}`}
                                    type="text"
                                    inputMode="url"
                                    placeholder="https://… site, página ou WhatsApp"
                                    value={item.link || ""}
                                    aria-invalid={linkInvalido(item.link)}
                                    onChange={(event) => updateItem(item.id, { link: event.target.value })}
                                  />
                                </Field>
                                {linkInvalido(item.link) && <LinkError role="alert">Link inválido: use o endereço completo do site ou do WhatsApp.</LinkError>}
                                <LinkShortcuts>
                                  {normalizarLink(profile.website) && (
                                    <ShortcutButton type="button" aria-label={`Usar site do perfil em ${item.nome}`} onClick={() => updateItem(item.id, { link: normalizarLink(profile.website) ?? "" })}>
                                      Site do perfil
                                    </ShortcutButton>
                                  )}
                                  {linkDoWhatsApp(profile.whatsapp || profile.phone) && (
                                    <ShortcutButton type="button" aria-label={`Usar WhatsApp em ${item.nome}`} onClick={() => updateItem(item.id, { link: linkDoWhatsApp(profile.whatsapp || profile.phone) ?? "" })}>
                                      WhatsApp
                                    </ShortcutButton>
                                  )}
                                </LinkShortcuts>
                              </div>
                            </LinkRow>
                          )}
                          {item.confirmar && (
                            <ConfirmLabel>
                              <input
                                aria-label={`Confirmar que ${item.nome} é oferecido`}
                                type="checkbox"
                                checked={item.confirmed}
                                onChange={(event) => updateItem(item.id, { confirmed: event.target.checked })}
                              />
                              <span>
                                Confirmo que o perfil realmente oferece este item.
                                {item.motivoConfirmar ? ` ${item.motivoConfirmar}` : ""}
                              </span>
                            </ConfirmLabel>
                          )}
                        </div>
                      </Linha>
                    );
                  })}
                </div>
              ) : (
                <Empty>A pesquisa não retornou itens para este tipo de atualização.</Empty>
              )}

              <PublishBar>
                <ToggleLabel>
                  <input aria-label="Simular" type="checkbox" checked={dryRun} onChange={(event) => setDryRun(event.target.checked)} />
                  <div>
                    Simular (não publica)
                    <span>
                      {dryRun
                        ? "MARCADO: a extensão preenche o formulário mas NÃO publica. Desmarque para publicar de verdade."
                        : "DESMARCADO: vai publicar de verdade no Google ao clicar."}
                    </span>
                  </div>
                </ToggleLabel>
                <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "#5F6368", minWidth: 170 }}>
                  ID do operador (o mesmo da extensão)
                  <input
                    type="text"
                    value={operatorId}
                    onChange={(e) => {
                      setOperatorId(e.target.value);
                      try {
                        localStorage.setItem("gr_operator_id", e.target.value);
                      } catch {
                        /* ignore */
                      }
                    }}
                    placeholder="ex.: thiago"
                    style={{ height: 36, padding: "0 10px", border: "1px solid #dadce0", borderRadius: 10, fontSize: 13, fontFamily: "inherit" }}
                  />
                </label>
                <Botao aria-label={dryRun ? "Simular atualização" : "Publicar atualização"} type="button" $variante={dryRun ? "tonal" : "filled"} onClick={publish} disabled={!canPublish}>
                  <span className="material-icons">{dryRun ? "science" : "publish"}</span>
                  {dryRun ? "Simular atualização" : "Publicar atualização"}
                </Botao>
              </PublishBar>
              {hasBlocked && (
                <Notice style={{ margin: 12 }}>
                  <span className="material-icons">warning</span>
                  Confirme todos os itens destacados antes de iniciar a atualização.
                </Notice>
              )}
              {hasProductWithoutPhoto && (
                <Notice style={{ margin: 12 }}>
                  <span className="material-icons">image_not_supported</span>
                  Adicione uma foto para cada produto selecionado.
                </Notice>
              )}
              {hasInvalidLink && (
                <Notice style={{ margin: 12 }}>
                  <span className="material-icons">link_off</span>
                  Corrija o link dos produtos marcados antes de continuar.
                </Notice>
              )}
            </Cartao>
          </>
        ) : (
          <Cartao>
            <Empty>Clique em “Pesquisar oportunidades com IA” para iniciar a atualização deste perfil.</Empty>
          </Cartao>
        )}

        {(["services", "service_descriptions", "products"] as Mode[]).map((modo) => {
          const job = jobs[modo];
          if (!job) return null;
          const progress = job.total ? Math.round((job.done / job.total) * 100) : 0;
          return (
            <Cartao key={modo}>
              <CabecalhoDoCartao>
                <span className="titulo">{`Progresso · ${TITULO_DO_MODO[modo]} · ${job.done}/${job.total}`}</span>
                <Selo $tom={job.status === "COMPLETED" ? "verde" : "azul"}>{job.status === "COMPLETED" ? "Concluído" : "Em andamento"}</Selo>
              </CabecalhoDoCartao>
              <CorpoDoCartao>
                <ProgressTrack>
                  <ProgressFill $value={progress} />
                </ProgressTrack>
                <Hint>{job.current ? `Processando: ${job.current}` : job.status === "COMPLETED" ? "Processo concluído." : "Preparando execução..."}</Hint>
                <Log>{job.log.join("\n") || "Aguardando o primeiro evento..."}</Log>
              </CorpoDoCartao>
            </Cartao>
          );
        })}
      </Coluna>

      <Aside>
        <AsideCard>
          <span style={{ fontFamily: "var(--font-sans)", fontSize: 15, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
            <span className="material-icons" style={{ color: "#0B4FA8" }}>extension</span>
            Como será publicado
          </span>
          <Passo><b>1</b><span>Você aprova o plano nesta tela.</span></Passo>
          <Passo><b>2</b><span>A extensão GeoRanking, aberta no Chrome do operador logado na conta do perfil, executa o cadastro na ficha.</span></Passo>
          <Passo><b>3</b><span>Serviços entram primeiro; produtos só com foto e link válidos.</span></Passo>
          <Passo><b>4</b><span>{`Respeita o limite de ${counts.dailyCap} itens por dia. O que sobrar fica agendado para amanhã.`}</span></Passo>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, paddingTop: 4 }}>
            <Hint>Conta do perfil</Hint>
            <span style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{googleConn?.email || "não vinculada"}</span>
          </div>
        </AsideCard>
        <Notice>
          <span className="material-icons">shield</span>
          Nome e categoria principal nunca são alterados. Serviços duvidosos pedem confirmação. Duplicidades são eliminadas antes de publicar.
        </Notice>
        {(primeiroServico || primeiroProduto) && (
          <AsideCard style={{ gap: 10 }}>
            <Lab>Simulação · como aparece na ficha</Lab>
            <div style={{ border: "1px solid #E2E6EA", borderRadius: 12, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
              {primeiroServico && (
                <>
                  <span style={{ fontSize: 12, color: "#5F6368" }}>Serviços</span>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 14 }}>
                    <span style={{ fontWeight: 500 }}>{primeiroServico.nome}</span>
                    <Hint>Sob consulta</Hint>
                  </div>
                  <Hint>{primeiroServico.descricao}</Hint>
                </>
              )}
              {primeiroServico && primeiroProduto && <div style={{ height: 1, background: "#EEF1F4" }} />}
              {primeiroProduto && (
                <>
                  <span style={{ fontSize: 12, color: "#5F6368" }}>Produtos</span>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <div style={{ width: 48, height: 40, borderRadius: 8, background: "#EAF1FB", color: "#0B4FA8", display: "inline-flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
                      {primeiroProduto.image ? <img src={primeiroProduto.image.dataUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span className="material-icons" style={{ fontSize: 18 }}>inventory_2</span>}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                      <span style={{ fontSize: 14, fontWeight: 500 }}>{primeiroProduto.nome}</span>
                      <Hint>{BOTOES_DO_PRODUTO.find((botao) => botao.valor === (primeiroProduto.botao || "SAIBA_MAIS"))?.rotulo || "Saiba mais"}</Hint>
                    </div>
                  </div>
                </>
              )}
            </div>
          </AsideCard>
        )}
        <AsideCard style={{ gap: 8 }}>
          <Lab>Pendências antes de publicar</Lab>
          {pendencias.length === 0 && <Hint>Nenhuma pendência nos itens selecionados.</Hint>}
          {pendencias.slice(0, 6).map((pendencia, indice) => (
            <Hint key={indice} style={{ display: "flex", gap: 8 }}>
              <span className="material-icons" style={{ fontSize: 18, color: pendencia.tom === "erro" ? "#B3261E" : "#B06000" }}>{pendencia.tom === "erro" ? "error" : "warning"}</span>
              {pendencia.texto}
            </Hint>
          ))}
        </AsideCard>
      </Aside>
    </EditorGrid>
  );
};
